import React, { FC, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSetHouseNumberMutation } from "../../api/projectsApi";
import Form from "../../components/Form/Form";
import { Span } from "../../components/Text/Text";
import { FormFieldT, TabOptionT, VersionT } from "../../models/models";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { convertNumToMinSec } from "../../utils/helpers/convertNumToMinSec";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import usePopup from "../../utils/hooks/usePopup";
import { MARK_NOT_SUITABLE_FORM_ID } from "./markNotSuitableFormId";
import {
  getMarkNotSuitableFormDirty,
  useMarkNotSuitableFormDirtyState,
} from "./markNotSuitableFormDirtyContext";
import { useMarkNotSuitableRefetchProject } from "./markNotSuitableRefetchContext";

import styles from "./markNotSuitableForBroadcasting.module.scss";


type MarkNotSuitableForBroadcastingBodyPropTypes = {
  versionData: VersionT;
};

const MarkNotSuitableForBroadcastingBody: FC<
  MarkNotSuitableForBroadcastingBodyPropTypes
> = ({ versionData }) => {
  const refetchProject = useMarkNotSuitableRefetchProject();
  const { setFormDirty } = useMarkNotSuitableFormDirtyState();

  const [dataVersion, setDataVersion] = useState<VersionT>(() => ({
    ...versionData,
  }));
  const initialVersionRef = useRef(versionData);

  useEffect(() => {
    setFormDirty(
      getMarkNotSuitableFormDirty(dataVersion, initialVersionRef.current)
    );
  }, [dataVersion, setFormDirty]);

  const reasonTabOptions: TabOptionT[] = [
    { value: 0, label: "Technical" },
    { value: 1, label: "Other" },
  ];
  const saveAsTabOptions: TabOptionT[] = [
    { value: 0, label: "Private" },
    { value: 1, label: "Public" },
  ];

  const fields: FormFieldT[] = [
    {
      label: "Mark this version as not suitable for broadcasting",
      name: "notSuitable",
      defaultValue: dataVersion.NotSuitableForBroadcast,
      type: "checkbox",
      onChange: (v) =>
        setDataVersion((prev) => ({ ...prev, NotSuitableForBroadcast: v })),
      style: !isMobileOrTablet() && { gridRow: 1, gridColumn: "span 2" },
    },
    {
      label: dataVersion.NotSuitableForBroadcast ? "Reason:" : null,
      defaultValue: dataVersion.NotSuitableForBroadcastReason,
      tabOptions: reasonTabOptions,
      onChange: (v) =>
        setDataVersion((prev) => ({ ...prev, NotSuitableForBroadcastReason: v })),
    },
    {
      label: dataVersion.NotSuitableForBroadcast ? "Save as:" : null,
      defaultValue:
        dataVersion.NotSuitableForBroadcastType !== undefined
          ? dataVersion.NotSuitableForBroadcastType
          : 1,
      tabOptions: saveAsTabOptions,
      onChange: (v) =>
        setDataVersion((prev) => ({ ...prev, NotSuitableForBroadcastType: v })),
    },
    {
      label: dataVersion.NotSuitableForBroadcast ? "Comment" : null,
      type: "textarea",
      defaultValue: (dataVersion.NotSuitableForBroadcastComment || "")
        .split("|")[0],
      style:
        !isMobileOrTablet() && { gridColumn: "span 2", marginTop: 20 },
      onChange: (v) =>
        setDataVersion((prev) => ({
          ...prev,
          NotSuitableForBroadcastComment: v,
        })),
    },
  ];

  const [setHouseNumber] = useSetHouseNumberMutation();
  const { removePopup } = usePopup();

  const handleSubmit = async () => {
    const body = {
      HouseNumber: dataVersion.HouseNumber,
      sponsershipHouseNumber: dataVersion.sponsershipHouseNumber,
      NotSuitableForBroadcast: dataVersion.NotSuitableForBroadcast,
      NotSuitableForBroadcastComment:
        dataVersion.NotSuitableForBroadcastComment,
      NotSuitableForBroadcastReason: dataVersion.NotSuitableForBroadcastReason,
      NotSuitableForBroadcastType: dataVersion.NotSuitableForBroadcastType,
      ProjectType: "VideoAd",
      useFor2Spots: dataVersion.useFor2Spots,
      Id: dataVersion.Id,
      FileType: dataVersion.FileType,
      SecondHouseNumberType: dataVersion.sponsershipHouseNumber
        ? dataVersion.SecondHouseNumberType
        : 0,
    };

    try {
      await setHouseNumber(body);
      await refetchProject?.();
      toast("Successfully changed", {
        type: "success",
        autoClose: TOAST_DURATION,
      });
      removePopup();
    } catch (e: unknown) {
      console.log(e);
    }
  };

  return (
    <>
      <div className={styles.versionInfo}>
        <p>
          <Span>AD-ID</Span>: {dataVersion.AdId}
        </p>
        <p>
          <Span>Version name</Span>: {dataVersion.Name}
        </p>
        <p>
          <Span>Length: </Span>
          {convertNumToMinSec(dataVersion.RequiredLength || 0)}
        </p>
      </div>
      <Form
        fields={fields}
        formID={MARK_NOT_SUITABLE_FORM_ID}
        onSubmit={handleSubmit}
        className={styles.formWrapper}
      />
    </>
  );
};

export default MarkNotSuitableForBroadcastingBody;
